import { AnyAction } from "@reduxjs/toolkit";
import { signInAsync, signOutAsync } from "../auth/auth.actions";
import {
  fetchEisFundInvestments,
  loadMoreEisFundInvestments,
  clearEisFundCache,
} from "./eisFund.actions";
import {
  EisFundState,
  CommitmentInvestmentsCache,
  DealCache,
  INITIAL_COMMITMENT_CACHE,
  INITIAL_EIS_FUND_STATE,
  EisFundFulfilledPayload,
  EisFundRejectedPayload,
} from "./eisFund.types";

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get or create commitment cache within a deal
 */
const getCommitmentCache = (
  state: EisFundState,
  dealId: string,
  commitmentId: string
): CommitmentInvestmentsCache => {
  return (
    state.deals[dealId]?.commitments?.[commitmentId] || {
      ...INITIAL_COMMITMENT_CACHE,
    }
  );
};

/**
 * Update commitment cache immutably.
 * Path: eisFund.deals[dealId].commitments[commitmentId]
 */
const updateCommitmentCache = (
  state: EisFundState,
  dealId: string,
  commitmentId: string,
  updates: Partial<CommitmentInvestmentsCache>
): EisFundState => {
  const existingDealCache: DealCache = state.deals[dealId] || { commitments: {} };
  const existingCommitmentCache =
    existingDealCache.commitments[commitmentId] || { ...INITIAL_COMMITMENT_CACHE };

  return {
    ...state,
    deals: {
      ...state.deals,
      [dealId]: {
        ...existingDealCache,
        commitments: {
          ...existingDealCache.commitments,
          [commitmentId]: {
            ...existingCommitmentCache,
            ...updates,
          },
        },
      },
    },
  };
};

// ============================================================================
// Reducer
// ============================================================================

const eisFundReducer = (
  state = INITIAL_EIS_FUND_STATE,
  action: AnyAction
): EisFundState => {
  switch (action.type) {
    // ========================================================================
    // Auth: Reset on sign in/out
    // ========================================================================
    case signInAsync.fulfilled.type:
    case signOutAsync.fulfilled.type:
      return { ...INITIAL_EIS_FUND_STATE };

    // ========================================================================
    // Fetch Investments
    // ========================================================================
    case fetchEisFundInvestments.pending.type: {
      const { dealId, commitmentId } = action.meta.arg;
      return updateCommitmentCache(state, dealId, commitmentId, {
        status: "loading",
        error: null,
      });
    }

    case fetchEisFundInvestments.fulfilled.type: {
      const {
        dealId,
        commitmentId,
        investments,
        pagination,
        filterKey,
      } = action.payload as EisFundFulfilledPayload;

      return updateCommitmentCache(state, dealId, commitmentId, {
        investments,
        pagination,
        status: "success",
        error: null,
        lastFetched: Date.now(),
        filterKey,
      });
    }

    case fetchEisFundInvestments.rejected.type: {
      const payload = (action.payload || action.meta.arg) as EisFundRejectedPayload;
      const { dealId, commitmentId, error } = payload;

      return updateCommitmentCache(state, dealId, commitmentId, {
        status: "error",
        error: error || "Failed to fetch investments",
      });
    }

    // ========================================================================
    // Load More Investments (Pagination)
    // ========================================================================
    case loadMoreEisFundInvestments.pending.type: {
      const { dealId, commitmentId } = action.meta.arg;
      return updateCommitmentCache(state, dealId, commitmentId, {
        status: "loadingMore",
        error: null,
      });
    }

    case loadMoreEisFundInvestments.fulfilled.type: {
      const { dealId, commitmentId, investments, pagination } =
        action.payload as EisFundFulfilledPayload;
      const existingCache = getCommitmentCache(state, dealId, commitmentId);

      return updateCommitmentCache(state, dealId, commitmentId, {
        investments: [...existingCache.investments, ...investments],
        pagination,
        status: "success",
        error: null,
        lastFetched: Date.now(),
      });
    }

    case loadMoreEisFundInvestments.rejected.type: {
      const payload = (action.payload || action.meta.arg) as EisFundRejectedPayload;
      const { dealId, commitmentId, error } = payload;

      return updateCommitmentCache(state, dealId, commitmentId, {
        status: "error",
        error: error || "Failed to load more investments",
      });
    }

    // ========================================================================
    // Clear Cache
    // ========================================================================
    case clearEisFundCache.type: {
      const payload = action.payload as
        | { dealId?: string; commitmentId?: string }
        | undefined;

      // Clear all cache
      if (!payload) {
        return { ...INITIAL_EIS_FUND_STATE };
      }

      const { dealId, commitmentId } = payload;

      // Clear specific commitment within a deal
      if (dealId && commitmentId) {
        const dealCache = state.deals[dealId];
        if (!dealCache) return state;

        const newCommitments = { ...dealCache.commitments };
        delete newCommitments[commitmentId];

        // Remove deal entry if no commitments left
        if (Object.keys(newCommitments).length === 0) {
          const newDeals = { ...state.deals };
          delete newDeals[dealId];
          return { ...state, deals: newDeals };
        }

        return {
          ...state,
          deals: {
            ...state.deals,
            [dealId]: {
              ...dealCache,
              commitments: newCommitments,
            },
          },
        };
      }

      // Clear entire deal and all its commitments
      if (dealId) {
        const newDeals = { ...state.deals };
        delete newDeals[dealId];
        return { ...state, deals: newDeals };
      }

      return state;
    }

    default:
      return state;
  }
};

export default eisFundReducer;
export type { EisFundState };
